import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInstance';
import { toastError, toastSuccess, toastInfo, confirmDelete } from '../../utils/alertHelper';

const Eventview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [users, setUsers] = useState([]);
  const [editableUsers, setEditableUsers] = useState([]);
  const [visitors, setVisitors] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('member'); 

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`/api/event/${id}`);
      // handle API shape: { Status: 0, Message: 'Event not found' }
      if (res.data && (res.data.Status === 0 || res.data.Status === '0')) {
        toastError(res.data.Message || 'Event not found');
        navigate('/event');
        return;
      }

      setEvent(res.data.event || res.data.data || null);
      const fetchedUsers = res.data.users || res.data.Users || res.data.data?.users || [];
      setUsers(fetchedUsers);
      
      setEditableUsers(fetchedUsers.map(u => ({ ...(u || {}), _editing: false })));
      setVisitors(
        res.data.visitors ||
          res.data.Visitors ||
          res.data.event?.visitors ||
          []
      ); 
    } catch (error) {
      toastError('Failed to fetch event');
      navigate('/event');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvent();
  }, [id]);

  if (loading) return <div className="container mt-4">Loading...</div>;
  if (!event) return <div className="container mt-4">Event not found</div>;

  
  const presentCount = (editableUsers.length ? editableUsers : users).filter(
    (u) => (u.status || '').toLowerCase() === 'present'
  ).length;
  const absentCount = (editableUsers.length ? editableUsers : users).filter(
    (u) => (u.status || '').toLowerCase() === 'absent'
  ).length;

 
  // Handler: optimistic change first, then show confirmation (with timestamp).
  // If confirmed -> persist; if cancelled or error -> revert.
  const handleFieldChangeWithConfirm = async (index, field, newValue) => {
    const user = editableUsers[index];
    if (!user) return;
    const name = (user.member && `${user.member.firstName || ''} ${user.member.lastName || ''}`.trim()) || user.member?.email || 'User';
    const pretty = field === 'is_coming' ? (newValue === 'coming' ? 'Coming' : 'Not Coming') : (newValue === 'present' ? 'Present' : 'Absent');

    const oldValue = user[field];
    // optimistic update: apply change immediately and mark editing
    setEditableUsers(prev => {
      const copy = prev.slice();
      copy[index] = { ...copy[index], [field]: newValue, _editing: true };
      return copy;
    });

    const timeString = new Date().toLocaleString();
    const message = `${name} changed to ${pretty} at ${timeString}. Confirm to save, Cancel to revert.`;

    // show confirmation AFTER the change (so user sees the update in the UI)
    const res = await confirmDelete(message);

    if (!res.isConfirmed) {
      // user cancelled -> revert optimistic change
      setEditableUsers(prev => {
        const copy = prev.slice();
        copy[index] = { ...copy[index], [field]: oldValue, _editing: false };
        return copy;
      });
      toastInfo('Change cancelled');
      return;
    }

    // user confirmed -> persist
    try {
      await axios.put('/api/attendance/update', { id: user._id || user.id, [field]: newValue });
      toastSuccess('Attendance updated');
      setEditableUsers(prev => {
        const copy = prev.slice();
        copy[index] = { ...copy[index], _editing: false };
        return copy;
      });
    } catch (err) {
      // revert on error
      setEditableUsers(prev => {
        const copy = prev.slice();
        copy[index] = { ...copy[index], [field]: oldValue, _editing: false };
        return copy;
      });
      toastError('Failed to update attendance');
    }
  };
  // Save single attendance record via API
  const saveAttendance = async (attendanceId, updatedFields, index) => {
    try {
      // assume endpoint for updating attendance
      await axios.put('/api/attendance/update', { id: attendanceId, ...updatedFields });
      toastSuccess('Attendance updated');
      // mark as saved (clear _editing)
      setEditableUsers(prev => {
        const copy = prev.slice();
        copy[index] = { ...copy[index], _editing: false };
        return copy;
      });
    } catch (err) {
      toastError('Failed to update attendance');
    }
  };

  // Save all edited attendance records in bulk
  const saveAllEdited = async () => {
    const edits = editableUsers
      .map((u, idx) => ({ u, idx }))
      .filter(x => x.u._editing)
      .map(x => ({ id: x.u._id || x.u.id, data: { status: x.u.status, is_coming: x.u.is_coming }, idx: x.idx }));
    if (edits.length === 0) {
      toastInfo('No changes to save');
      return;
    }
    try {
      await axios.put('/api/attendance/update', { updates: edits.map(e => ({ id: e.id, ...e.data })) });
      toastSuccess('All changes saved');
      setEditableUsers(prev => prev.map(u => ({ ...u, _editing: false })));
    } catch (err) {
      toastError('Failed to save changes');
    }
  };

  return (
    <div className="container mt-4">
      <h4 className="mb-4">Scheduled Chapter Meeting</h4>
      <div className="card p-4 shadow-sm">
        <div className="row">
          <div className="col-md-6 mb-2">
            <strong>Meeting Name:</strong> {event.name}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Date:</strong> {event.startDate?.split('T')[0]}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Venue:</strong>{' '}
            {event.venue?.name ||
              event.meetingVenue?.venueName ||
              event.meetingVenue?.name ||
              '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Time:</strong> {event.formTime} To {event.endTime}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Reporting Time:</strong> {event.reportingTime || '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Closure Date:</strong> {event.closureDate ? (event.closureDate.split ? event.closureDate.split('T')[0] : event.closureDate) : '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Closure Time:</strong> {event.closureTime || '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Chapter:</strong>{' '}
            {Array.isArray(event.chapter)
              ? event.chapter.map((c) => c.name).join(', ')
              : '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Created by:</strong>{' '}
            {event.createdBy?.firstName || event.createdBy?.email || '-'}{' '}
            {event.createdBy?.lastName || ''}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Notes:</strong> {event.notes || '-'}
          </div>
          <div className="col-md-6 mb-2">
            <strong>Guest Fees:</strong> ₹
            {event.guestAllow ? event.guestFees : '-'}
          </div>
        </div>
      </div>
      {/* Tabs */}
      <ul className="nav nav-tabs mt-4">
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'member' ? ' active' : ''}`}
            type="button"
            onClick={() => setActiveTab('member')}
          >
            Member Attendance
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link${activeTab === 'visitor' ? ' active' : ''}`}
            type="button"
            onClick={() => setActiveTab('visitor')}
          >
            Visitor
          </button>
        </li>
      </ul>
      {/* Member Tab */}
      {activeTab === 'member' && (
        <div className="card p-4 shadow-sm">
          <h5 className="mb-3">Attendance</h5>
          <div className="row text-center mb-3">
            <div className="col">
              <strong className="text-success">{presentCount}</strong> Present
            </div>
            <div className="col">
              <strong className="text-danger">{absentCount}</strong> Absent
            </div>
            <div className="col">
              <strong>{users.length}</strong> Total
            </div>
          </div>
          {/* Save buttons removed — changes are saved on confirm */}
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Mobile Number</th>
                  <th>Chapter</th>
                  <th>Is Coming</th>
                  <th>Coming Person Type</th>
                  <th>Present/Absent</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {editableUsers.length > 0 ? (
                  editableUsers.map((attendance, index) => {
                    const member = attendance.member || {};
                    const memberName = [
                      member.firstName,
                      member.middleName,
                      member.lastName,
                    ]
                      .filter(Boolean)
                      .join(' ')
                      .trim();
                    const mobileNumber = member.mobileNumber || '';
                    const chapter = Array.isArray(member.chapter)
                      ? member.chapter.map((chap) => chap.name).join(', ')
                      : '';
                    const isComing = (attendance.is_coming || attendance.isComing || '').toString();
                    const presentStatus = (attendance.status || '').toString();
                    return (
                      <tr key={attendance._id || attendance.id || index}>
                        <td>{memberName || '-'}</td>
                        <td>{mobileNumber || '-'}</td>
                        <td>{chapter || '-'}</td>
                        <td>
                          <select className="form-select form-select-sm" value={isComing} onChange={e => handleFieldChangeWithConfirm(index, 'is_coming', e.target.value)}>
                            <option value="">Select</option>
                            <option value="coming">Coming</option>
                            <option value="not coming">Not Coming</option>
                          </select>
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={isComing} onChange={e => handleFieldChangeWithConfirm(index, 'is_coming', e.target.value)}>
                            <option value="">Select</option>
                            <option value="self">Self</option>
                            <option value="substitute">Substitute</option>
                          </select>
                        </td>
                        <td>
                          <select className="form-select form-select-sm" value={presentStatus} onChange={e => handleFieldChangeWithConfirm(index, 'status', e.target.value)}>
                            <option value="">Select</option>
                            <option value="present">Present</option>
                            <option value="absent">Absent</option>
                          </select>
                        </td>
                        <td>
                          {/* Remark is read-only now */}
                          <div>{attendance.reason || '—'}</div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No attendees found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Visitor Tab */}
      {activeTab === 'visitor' && (
        <div className="card p-4 shadow-sm">
          <h5 className="mb-3">Visitors</h5>
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Mobile Number</th>
                  <th>Business Category</th>
                  <th>Company Name</th>
                  <th>Invite By</th>
                </tr>
              </thead>
              <tbody>
                {visitors && visitors.length > 0 ? (
                  visitors.map((visitor, idx) => (
                    <tr key={visitor._id || idx}>
                      <td>
                        {visitor.name ||
                          [
                            visitor.firstName,
                            visitor.lastName,
                          ]
                            .filter(Boolean)
                            .join(' ')
                            .trim() ||
                          '-'}
                      </td>
                      <td>
                        {visitor.mobileNumber ||
                          visitor.phone ||
                          visitor.contactNumber ||
                          '-'}
                      </td>
                      <td>
                        {visitor.category || visitor.type || '-'}
                      </td>
                      <td>
                        {visitor.companyName || visitor.company || '-'}
                      </td>
                      <td>
                        {/* Invite by */}
                        {visitor.inviteBy?.name ||
                          visitor.inviteBy?.firstName ||
                          visitor.invitedBy?.name ||
                          visitor.invitedBy?.firstName ||
                          '-'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      No visitors found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Eventview;